import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import AnalyticsTracker from "@/components/AnalyticsTracker";

const Index = lazy(() => import("./pages/Index"));
const Eventos = lazy(() => import("./pages/Eventos"));
const Competicoes = lazy(() => import("./pages/Competicoes"));
const Membros = lazy(() => import("./pages/Membros"));
const Alumni = lazy(() => import("./pages/Alumni"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CompeticoesInternos = lazy(() => import("./pages/CompeticoesInternos"));
const CompeticoesExternos = lazy(() => import("./pages/CompeticoesExternos"));
const ConteudosExclusivos = lazy(() => import("./pages/ConteudosExclusivos"));
const Beneficios = lazy(() => import("./pages/Beneficios"));
const AdminBeneficios = lazy(() => import("./pages/AdminBeneficios"));
const AdminMembros = lazy(() => import("./pages/AdminMembros"));
const AdminChallenges = lazy(() => import("./pages/AdminChallenges"));
const AdminHighlights = lazy(() => import("./pages/AdminHighlights"));
const AdminParceiros = lazy(() => import("./pages/AdminParceiros"));
const Educacao = lazy(() => import("./pages/Educacao"));
const AdminEducacao = lazy(() => import("./pages/AdminEducacao"));
const ProcessoSeletivo = lazy(() => import("./pages/ProcessoSeletivo"));
const AdminProcessoSeletivo = lazy(() => import("./pages/AdminProcessoSeletivo"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminHome = lazy(() => import("./pages/AdminHome"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminInstagram = lazy(() => import("./pages/AdminInstagram"));
const AdminEventos = lazy(() => import("./pages/AdminEventos"));
const AdminContato = lazy(() => import("./pages/AdminContato"));
const AdminAlumniCompanies = lazy(() => import("./pages/AdminAlumniCompanies"));
const AdminAlumni = lazy(() => import("./pages/AdminAlumni"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminPageHeaders = lazy(() => import("./pages/AdminPageHeaders"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="text-sm text-muted-foreground font-medium">Carregando...</span>
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AnalyticsTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/eventos" element={<Eventos />} />
                <Route path="/competicoes" element={<Competicoes />} />
                <Route path="/competicoes/internos" element={<CompeticoesInternos />} />
                <Route path="/competicoes/externos" element={<CompeticoesExternos />} />
                <Route path="/membros" element={<Membros />} />
                <Route path="/alumni" element={<Alumni />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/conteudos" element={<ConteudosExclusivos />} />
                <Route path="/beneficios" element={<Beneficios />} />
                <Route path="/admin/beneficios" element={<AdminBeneficios />} />
                <Route path="/admin/membros" element={<AdminMembros />} />
                <Route path="/admin/challenges" element={<AdminChallenges />} />
                <Route path="/admin/destaques" element={<AdminHighlights />} />
                <Route path="/admin/parceiros" element={<AdminParceiros />} />
                <Route path="/educacao" element={<Educacao />} />
                <Route path="/admin/educacao" element={<AdminEducacao />} />
                <Route path="/processo-seletivo" element={<ProcessoSeletivo />} />
                <Route path="/admin/processo-seletivo" element={<AdminProcessoSeletivo />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/home" element={<AdminHome />} />
                <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                <Route path="/admin/instagram" element={<AdminInstagram />} />
                <Route path="/admin/eventos" element={<AdminEventos />} />
                <Route path="/admin/contato" element={<AdminContato />} />
                <Route path="/admin/alumni-companies" element={<AdminAlumniCompanies />} />
                <Route path="/admin/alumni" element={<AdminAlumni />} />
                <Route path="/admin/depoimentos" element={<AdminTestimonials />} />
                <Route path="/admin/page-headers" element={<AdminPageHeaders />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
